import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, FileText, Mail, Search, Trash2, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi, czk, errMsg, fmtDate, openBlob, STATUS } from "../lib/api";
import { Card, Field, PageTitle, useList } from "./ui";

const Drawer = ({ children, onClose, testid }) => (
  <motion.div className="fixed inset-0 z-50 flex justify-end bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
    <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 26 }} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-[#F7F3EA] p-6" data-testid={testid}>
      <button onClick={onClose} className="mb-4 ml-auto block rounded-full bg-white p-2" data-testid={`${testid}-close`}><X /></button>
      {children}
    </motion.div>
  </motion.div>
);

const OrderDetail = ({ o, onChange }) => {
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pdf = () => openBlob(adminApi, `/orders/${o.id}/invoice`).then((u) => window.open(u, "_blank"));
  const b = o.billing;
  return (
    <div className="space-y-4">
      <h2 className="h-display text-3xl">Objednávka {o.number}</h2>
      <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${STATUS[o.status].cls}`} data-testid="admin-order-status">{STATUS[o.status].label}</span>
      {o.status === "pending" && o.customer_reported_at && <p className="rounded-2xl bg-[#FDF3D3] p-3 text-sm">Zákazník {fmtDate(o.customer_reported_at)} nahlásil, že zaplatil převodem. Zkontrolujte příchozí platbu (VS {o.vs}) a potvrďte ji.</p>}
      <div className="flex flex-wrap gap-2">
        {o.status !== "paid" && <button className="btn-sage" onClick={() => act(() => adminApi.post(`/orders/${o.id}/mark-paid`), "Platba potvrzena – soubory zpřístupněny, faktura odeslána")} data-testid="admin-mark-paid-btn"><CheckCircle2 className="h-4 w-4" /> Potvrdit platbu</button>}
        {o.status === "pending" && <button className="btn-ghost" onClick={() => act(() => adminApi.post(`/orders/${o.id}/status`, { status: "cancelled" }), "Zrušeno")} data-testid="admin-cancel-btn"><XCircle className="h-4 w-4" /> Zrušit</button>}
        {o.status === "cancelled" && <button className="btn-ghost" onClick={() => act(() => adminApi.post(`/orders/${o.id}/status`, { status: "pending" }), "Obnoveno")}>Obnovit</button>}
        <button className="btn-ghost" onClick={pdf} data-testid="admin-invoice-btn"><FileText className="h-4 w-4" /> {o.invoice_number ? "Faktura" : "Proforma"}</button>
        <button className="btn-ghost" onClick={() => act(() => adminApi.post(`/orders/${o.id}/resend`), "E-mail odeslán")} data-testid="admin-resend-btn"><Mail className="h-4 w-4" /> Odeslat e-mail znovu</button>
      </div>
      <Card title="Zákazník">
        <p className="text-sm">{b.name}{b.company ? ` · ${b.company}` : ""}<br />{b.email}<br />{b.street} {b.zip} {b.city}{b.ico ? <><br />IČO {b.ico} {b.dic && `DIČ ${b.dic}`}</> : null}</p>
      </Card>
      <Card title="Položky">
        {o.items.map((i) => <div key={i.product_id} className="flex justify-between border-b py-2 text-sm"><span>{i.name} × {i.qty}</span><span>{czk(i.price * i.qty)}</span></div>)}
        {o.discount > 0 && <div className="flex justify-between py-2 text-sm text-[#D96B43]"><span>Sleva {o.coupon}</span><span>-{czk(o.discount)}</span></div>}
        {o.voucher_amount > 0 && <div className="flex justify-between py-2 text-sm text-[#D96B43]" data-testid="admin-order-voucher"><span>Dárkový poukaz {o.voucher}</span><span>-{czk(o.voucher_amount)}</span></div>}
        <div className="flex justify-between pt-2 font-bold"><span>Celkem</span><span>{czk(o.total)}</span></div>
      </Card>
      <Card><p className="text-sm text-[#6E7565]">Platba: {({ stripe: "Karta (Stripe)", comgate: "Comgate", voucher: "Plně uhrazeno dárkovým poukazem", free: "Zdarma (bez platby)" })[o.payment_method] || "Převod / QR"} · VS {o.vs}<br />Vytvořeno: {fmtDate(o.created_at)} · Zaplaceno: {fmtDate(o.paid_at)}<br />Faktura: {o.invoice_number || "–"}{o.note ? <><br />Poznámka: {o.note}</> : null}</p></Card>
    </div>
  );
};

export function Orders() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [list, load] = useList(`/orders?${new URLSearchParams({ ...(status && { status }), ...(q && { q }) })}`);
  const [sel, setSel] = useState(null);
  const refresh = async () => {
    await load();
    const { data } = await adminApi.get(`/orders?q=${sel.number}`);
    setSel(data[0] || null);
  };
  const del = async (o) => {
    if (!window.confirm(`Smazat objednávku ${o.number}?`)) return;
    await adminApi.delete(`/orders/${o.id}`);
    load();
  };
  return (
    <div data-testid="admin-orders">
      <PageTitle title="Objednávky" sub="Potvrzení plateb, faktury a e-maily" />
      <div className="mb-4 flex flex-wrap gap-2">
        {[["", "Vše"], ["pending", "Čeká na platbu"], ["paid", "Zaplaceno"], ["cancelled", "Zrušeno"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-4 py-2 text-sm ${status === v ? "bg-[#7A8460] text-white" : "border bg-white"}`} data-testid={`orders-filter-${v || "all"}`}>{l}</button>
        ))}
        <div className="ml-auto flex items-center gap-2 rounded-full border bg-white px-4 py-2"><Search className="h-4 w-4" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Číslo, jméno, e-mail" className="bg-transparent text-sm outline-none" data-testid="orders-search" /></div>
      </div>
      <div className="overflow-x-auto rounded-3xl border bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#F7F3EA] text-left text-xs uppercase tracking-wider text-[#6E7565]"><tr><th className="p-3">Číslo</th><th>Zákazník</th><th>Datum</th><th>Platba</th><th>Stav</th><th className="text-right">Částka</th><th /></tr></thead>
          <tbody className="divide-y">
            {(list || []).map((o) => (
              <tr key={o.id} className="cursor-pointer hover:bg-[#F7F3EA]/60" onClick={() => setSel(o)} data-testid={`admin-order-row-${o.number}`}>
                <td className="p-3 font-semibold">{o.number}</td><td>{o.billing.name}<br /><span className="text-xs text-[#6E7565]">{o.billing.email}</span></td>
                <td>{fmtDate(o.created_at)}</td><td>{{ stripe: "Karta", qr: "QR převod", comgate: "Comgate", voucher: "Poukaz", free: "Zdarma" }[o.payment_method]}</td>
                <td><span className={`rounded-full px-3 py-1 text-xs ${STATUS[o.status].cls}`}>{STATUS[o.status].label}</span>{o.status === "pending" && o.customer_reported_at && <span className="ml-1 rounded-full bg-[#F2C230] px-2 py-1 text-[10px] font-bold" data-testid={`reported-badge-${o.number}`}>Zákazník hlásí platbu</span>}</td>
                <td className="text-right font-bold">{czk(o.total)}</td>
                <td className="pr-3 text-right"><button onClick={(e) => { e.stopPropagation(); del(o); }} className="rounded-full p-2 text-[#D96B43] hover:bg-[#F8E1D7]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list && !list.length && <p className="p-8 text-center text-[#6E7565]">Žádné objednávky.</p>}
      </div>
      <AnimatePresence>{sel && <Drawer onClose={() => setSel(null)} testid="admin-order-drawer"><OrderDetail o={sel} onChange={refresh} /></Drawer>}</AnimatePresence>
    </div>
  );
}

export function Invoices() {
  const [list] = useList("/invoices");
  const [q, setQ] = useState("");
  const view = (o) => openBlob(adminApi, `/orders/${o.id}/invoice`).then((u) => window.open(u, "_blank"));
  const download = (o) => openBlob(adminApi, `/orders/${o.id}/invoice`, `faktura-${o.invoice_number}.pdf`);
  const resend = async (o) => {
    try {
      const { data } = await adminApi.post(`/orders/${o.id}/resend`);
      data.sent ? toast.success("Faktura odeslána zákazníkovi") : toast.error("Odeslání se nezdařilo – viz Odeslané e-maily");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = (list || []).filter((o) => `${o.invoice_number} ${o.number} ${o.billing.name} ${o.billing.email}`.toLowerCase().includes(q.toLowerCase()));
  const sum = shown.reduce((s, o) => s + o.total, 0);
  return (
    <div data-testid="admin-invoices">
      <PageTitle title="Faktury" sub={`${shown.length} vystavených dokladů · celkem ${czk(sum)}`} action={<div className="flex items-center gap-2 rounded-full border bg-white px-4 py-2"><Search className="h-4 w-4" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Číslo, zákazník" className="bg-transparent text-sm outline-none" data-testid="invoices-search" /></div>} />
      <div className="overflow-x-auto rounded-3xl border bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#F7F3EA] text-left text-xs uppercase tracking-wider text-[#6E7565]"><tr><th className="p-3">Faktura</th><th>Objednávka</th><th>Zákazník</th><th>Vystaveno</th><th className="text-right">Částka</th><th /></tr></thead>
          <tbody className="divide-y">
            {shown.map((o) => (
              <tr key={o.id} data-testid={`invoice-row-${o.invoice_number}`}>
                <td className="p-3 font-semibold">{o.invoice_number}</td><td>{o.number}</td><td>{o.billing.name}<br /><span className="text-xs text-[#6E7565]">{o.billing.email}</span></td>
                <td>{fmtDate(o.paid_at)}</td><td className="text-right font-bold">{czk(o.total)}</td>
                <td className="whitespace-nowrap pr-3 text-right">
                  <button onClick={() => view(o)} className="rounded-full px-3 py-1.5 text-xs hover:bg-[#E9EFE0]" data-testid={`invoice-view-${o.invoice_number}`}>Zobrazit</button>
                  <button onClick={() => download(o)} className="rounded-full px-3 py-1.5 text-xs hover:bg-[#E9EFE0]" data-testid={`invoice-download-${o.invoice_number}`}>Stáhnout</button>
                  <button onClick={() => resend(o)} className="rounded-full bg-[#7A8460] px-3 py-1.5 text-xs text-white" data-testid={`invoice-resend-${o.invoice_number}`}><Mail className="inline h-3 w-3" /> Odeslat znovu</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list && !shown.length && <p className="p-8 text-center text-[#6E7565]">Zatím žádné faktury – vystaví se automaticky po potvrzení platby.</p>}
      </div>
    </div>
  );
}

const CustomerDetail = ({ id, onSaved }) => {
  const [list] = useList(`/customers/${id}`);
  const [f, setF] = useState(null);
  const u = f || list;
  if (!u) return null;
  const save = async () => {
    try {
      await adminApi.put(`/customers/${id}`, u);
      toast.success("Uloženo");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const fld = (k, l) => <Field label={l} value={u[k] || ""} onChange={(e) => setF({ ...u, [k]: e.target.value })} testid={`customer-${k}`} />;
  return (
    <div className="space-y-4">
      <h2 className="h-display text-3xl">{u.name || u.email}</h2>
      <Card title="Údaje"><div className="grid gap-3 sm:grid-cols-2">{fld("name", "Jméno")}{fld("email", "E-mail")}{fld("phone", "Telefon")}{fld("street", "Ulice")}{fld("city", "Město")}{fld("zip", "PSČ")}{fld("company", "Firma")}{fld("ico", "IČO")}</div>
        <button className="btn-sage mt-4" onClick={save} data-testid="customer-save-btn">Uložit</button></Card>
      <Card title={`Objednávky (${list.order_list?.length || 0})`}>
        {(list.order_list || []).map((o) => <div key={o.id} className="flex justify-between border-b py-2 text-sm"><span>{o.number} · {fmtDate(o.created_at)}</span><span className={`rounded-full px-2 text-xs ${STATUS[o.status].cls}`}>{STATUS[o.status].label}</span><b>{czk(o.total)}</b></div>)}
      </Card>
    </div>
  );
};

export function Customers() {
  const [list, load] = useList("/customers");
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const del = async (u) => {
    if (!window.confirm(`Smazat zákazníka ${u.email}?`)) return;
    await adminApi.delete(`/customers/${u.id}`);
    load();
  };
  const shown = (list || []).filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="admin-customers">
      <PageTitle title="Zákazníci" sub={`${list?.length || 0} registrovaných`} action={<div className="flex items-center gap-2 rounded-full border bg-white px-4 py-2"><Search className="h-4 w-4" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat" className="bg-transparent text-sm outline-none" data-testid="customers-search" /></div>} />
      <div className="overflow-x-auto rounded-3xl border bg-white">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-[#F7F3EA] text-left text-xs uppercase tracking-wider text-[#6E7565]"><tr><th className="p-3">Zákazník</th><th>Registrace</th><th>Přihlášení</th><th>Objednávky</th><th className="text-right">Utraceno</th><th /></tr></thead>
          <tbody className="divide-y">
            {shown.map((u) => (
              <tr key={u.id} className="cursor-pointer hover:bg-[#F7F3EA]/60" onClick={() => setSel(u.id)} data-testid={`admin-customer-row-${u.email}`}>
                <td className="p-3"><b>{u.name}</b><br /><span className="text-xs text-[#6E7565]">{u.email}</span></td><td>{fmtDate(u.created_at)}</td><td>{u.provider === "google" ? "Google" : "E-mail"}</td><td>{u.orders}</td><td className="text-right font-bold">{czk(u.spent)}</td>
                <td className="pr-3 text-right"><button onClick={(e) => { e.stopPropagation(); del(u); }} className="rounded-full p-2 text-[#D96B43] hover:bg-[#F8E1D7]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AnimatePresence>{sel && <Drawer onClose={() => setSel(null)} testid="admin-customer-drawer"><CustomerDetail id={sel} onSaved={load} /></Drawer>}</AnimatePresence>
    </div>
  );
}
